import { Route } from 'next'
import { appTrace } from '~/utils'
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import type { MouseEvent, KeyboardEvent } from 'react'

/**
 * Represents a navigation action with a name, route, and optional event.
 * Used for tracking navigation events and performing navigation.
 */
export type NavigationAction = {
  /** A descriptive name for the navigation action, used for logging and tracking */
  name: string
  /** The route to navigate to */
  route: Route
  /**
   * Optional event that triggered the navigation (mouse or keyboard).
   * Only required for user-initiated navigation (clicks, keyboard events).
   * Can be omitted for programmatic navigation (e.g., auth redirects, timeouts).
   */
  event?: MouseEvent | KeyboardEvent
}

/**
 * Represents a log entry for a navigation action.
 * Used for tracking navigation history in tests.
 */
export type NavigationLogEntry = {
  /** The name of the navigation action */
  name: string
  /** The route that was navigated to */
  route: Route
  /** Timestamp of when the navigation occurred */
  timestamp: number
}

// Store navigation log in window object so it's accessible to tests
declare global {
  interface Window {
    __navigationLog?: NavigationLogEntry[]
  }
}

// Initialize the log in a client-side effect
function initializeNavigationLog() {
  if (typeof window !== 'undefined') {
    window.__navigationLog = window.__navigationLog || []
  }
}

// Call initialization on module load if we're in the browser
if (typeof window !== 'undefined') {
  initializeNavigationLog()
}

/**
 * Helper interface for navigation functionality.
 * Provides methods for navigating, tracking navigation history, and clearing history.
 */
export type NavigationHelper = {
  /**
   * Navigate to a new route.
   * @param action - The navigation action to perform
   * @param action.name - A descriptive name for the navigation action
   * @param action.route - The route to navigate to
   * @param action.event - Optional event that triggered the navigation. Only required for user-initiated navigation.
   */
  navigate: (action: NavigationAction) => void
  /**
   * Get the history of navigation actions.
   * @returns Array of navigation log entries
   */
  getHistory: () => NavigationLogEntry[]
  /**
   * Clear the navigation history.
   */
  clearHistory: () => void
  /**
   * Get the most recent navigation action.
   * @returns The last navigation log entry or null if no history exists
   */
  getLastNavigation: () => NavigationLogEntry | null
}

/**
 * Creates a navigation helper instance.
 * @param router - The Next.js router instance
 * @returns A navigation helper with methods for navigation and history tracking
 */
export function createNavigationHelper(router: AppRouterInstance): NavigationHelper {
  function navigate({ name, route, event }: NavigationAction) {
    // Log the navigation action
    appTrace(`navigation: ${name}`, { route })

    // Track navigation for tests
    if (typeof window !== 'undefined') {
      // Ensure log is initialized
      initializeNavigationLog()
      window.__navigationLog?.push({
        name,
        route,
        timestamp: Date.now(),
      })
    }

    // Prevent default if event is provided
    if (event) {
      event.preventDefault()
    }

    // Perform the navigation using the latest router instance
    router.push(route)
  }

  function getHistory() {
    if (typeof window !== 'undefined') {
      initializeNavigationLog()
      return [...(window.__navigationLog || [])]
    }
    return []
  }

  function clearHistory() {
    if (typeof window !== 'undefined') {
      initializeNavigationLog()
      window.__navigationLog = []
    }
  }

  function getLastNavigation() {
    if (typeof window !== 'undefined') {
      initializeNavigationLog()
      const log = window.__navigationLog
      return log && log.length > 0 ? log[log.length - 1] : null
    }
    return null
  }

  return {
    navigate,
    getHistory,
    clearHistory,
    getLastNavigation,
  }
}
